import React, { useState } from 'react';

import { Button, DrawerProps, Group, Stack, Text, Title } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import dayjs from 'dayjs';
import useUpcomingRevenueProjection from 'hooks/reports/revenue/useUpcomingRevenueProjection';

import RevenueProjectionEmptyState from './RevenueProjectionEmptyState';
import SelectRevenueProjectionDateRangeModal from './SelectRevenueProjectionDateRangeModal';
import { RevenueProjectionDateRangeFormValues } from './types';
import UpcomingRevenueProjectionResult from './UpcomingRevenueProjectionResult';

interface UpcomingRevenueDrawerProps extends Pick<DrawerProps, 'opened' | 'onClose'> {}

const UpcomingRevenueProjectionDrawer = ({ opened, onClose }: UpcomingRevenueDrawerProps): JSX.Element => {
  const [
    isSelectDateRangeModalOpened,
    { open: openSelectDateRangeModalOpened, close: closeSelectDateRangeModalOpened },
  ] = useDisclosure();

  const defaultValue = {
    startDate: null,
    endDate: null,
  };

  const [revenueProjectionDateRangeFormValuesToRun, setRevenueProjectionDateRangeFormValuesToRun] =
    useState<RevenueProjectionDateRangeFormValues>(defaultValue);
  const [debouncedRevenueProjectionDateRangeFormValuesToRun] =
    useDebouncedValue<RevenueProjectionDateRangeFormValues>(revenueProjectionDateRangeFormValuesToRun, 250);

  const { isLoading, isFetching, data } = useUpcomingRevenueProjection({
    startDate: debouncedRevenueProjectionDateRangeFormValuesToRun.startDate
      ? dayjs(debouncedRevenueProjectionDateRangeFormValuesToRun.startDate)
          .startOf('month')
          .format('YYYY-MM-DD')
      : null,
    endDate: debouncedRevenueProjectionDateRangeFormValuesToRun.endDate
      ? dayjs(debouncedRevenueProjectionDateRangeFormValuesToRun.endDate).endOf('month').format('YYYY-MM-DD')
      : null,
  });

  const hasRevenueProjectionDateRangeValues = !!(
    debouncedRevenueProjectionDateRangeFormValuesToRun.startDate &&
    debouncedRevenueProjectionDateRangeFormValuesToRun.endDate
  );

  const handleClose = (): void => {
    setRevenueProjectionDateRangeFormValuesToRun(defaultValue);
    onClose();
  };

  const isLoadingOrFetching = isLoading || isFetching;

  return (
    <Drawer
      title={
        <Group>
          <Title order={3}>Revenue Projection Tool</Title>
        </Group>
      }
      opened={opened}
      onClose={handleClose}
      size='xl'
    >
      {!hasRevenueProjectionDateRangeValues && <RevenueProjectionEmptyState />}

      {hasRevenueProjectionDateRangeValues && (
        <UpcomingRevenueProjectionResult
          dateRangeValues={debouncedRevenueProjectionDateRangeFormValuesToRun}
          revenueProjectionData={data?.data}
          isLoadingOrFetching={isLoadingOrFetching}
        />
      )}

      {!isLoadingOrFetching && (
        <>
          <Stack mt='xl'>
            {hasRevenueProjectionDateRangeValues && (
              <Text ta='center' c='dark.3'>
                Want to run a New Revenue Projection?
              </Text>
            )}
            <Button size='md' variant='subtle' onClick={openSelectDateRangeModalOpened}>
              Select Future Dates
            </Button>
          </Stack>
        </>
      )}

      <SelectRevenueProjectionDateRangeModal
        opened={isSelectDateRangeModalOpened}
        onClose={closeSelectDateRangeModalOpened}
        setRevenueProjectionDateRangeFormValuesToRun={setRevenueProjectionDateRangeFormValuesToRun}
      />
    </Drawer>
  );
};

export default UpcomingRevenueProjectionDrawer;
